import { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import axios from 'axios';
import { toast } from 'sonner';

const BLUE = "linear-gradient(120deg,#1D4ED8,#2563EB)";

const AdminUserEditModal = ({ user, isOpen, onClose, onSaved }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        business_name: user.business_name || '',
        address: user.address || '',
        abn: user.abn || '',
        gst_registered: user.gst_registered || false,
        bio: user.bio || '',
        website_url: user.website_url || '',
        account_status: user.account_status || 'active',
        approval_status: user.approval_status || 'pending',
        skills: user.skills || [],
        wwcc: user.wwcc || false,
        casa_repl: user.casa_repl || false,
        casa_reoc: user.casa_reoc || false
      });
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const roleOptions = [
    { value: 'photographer', label: 'Photographer' },
    { value: 'videographer', label: 'Videographer' },
    { value: 'editor_photos', label: 'Photo Editor' },
    { value: 'editor_video', label: 'Video Editor' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'drone_operator', label: 'Drone Operator' },
    { value: 'voxpop_videographer', label: 'VoxPop Videographer' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(role)
        ? prev.skills.filter(r => r !== role)
        : [...prev.skills, role]
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/update`,
        formData
      );

      if (response.data.success) {
        toast.success('User updated successfully');
        onSaved();
        onClose();
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const label = "text-xs font-semibold text-[#2B3142] mb-1.5 block";
  const select = "w-full h-[42px] px-3 rounded-[10px] border border-[#E0E3EB] text-[13px] bg-white outline-none focus:border-[#28C0D0]";

  return (
    <div className="sz-modal-backdrop sz-fade" onClick={onClose}>
      <div className="sz-modal-card w-full max-w-[600px]" onClick={(e) => e.stopPropagation()}>
        {/* Header (blue = edit) */}
        <div className="flex items-center justify-between px-6 py-[18px] text-white" style={{ background: BLUE }}>
          <div className="min-w-0">
            <h3 className="text-[17px] font-bold m-0 truncate" style={{ fontFamily: "Poppins" }}>Edit User: {user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email}</h3>
            <p className="text-white/80 text-xs mt-0.5">USR-{user.user_number}</p>
          </div>
          <button onClick={onClose} className="w-[30px] h-[30px] rounded-[9px] bg-white/20 hover:bg-white/30 text-white flex items-center justify-center shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="flex-1 overflow-y-auto szscroll px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <Label className={label}>First Name *</Label>
              <Input value={formData.first_name} onChange={(e) => handleChange('first_name', e.target.value)} required />
            </div>
            <div>
              <Label className={label}>Last Name *</Label>
              <Input value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} required />
            </div>
            <div>
              <Label className={label}>Email *</Label>
              <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required />
            </div>
            <div>
              <Label className={label}>Phone</Label>
              <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} />
            </div>
            <div>
              <Label className={label}>Business Name</Label>
              <Input value={formData.business_name} onChange={(e) => handleChange('business_name', e.target.value)} />
            </div>
            <div>
              <Label className={label}>ABN</Label>
              <Input value={formData.abn} onChange={(e) => handleChange('abn', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Label className={label}>Address</Label>
              <Input value={formData.address} onChange={(e) => handleChange('address', e.target.value)} />
            </div>
            <div>
              <Label className={label}>Website URL</Label>
              <Input type="url" value={formData.website_url} onChange={(e) => handleChange('website_url', e.target.value)} />
            </div>
            <div className="flex items-center gap-2 pt-7">
              <input type="checkbox" id="gst" checked={formData.gst_registered} onChange={(e) => handleChange('gst_registered', e.target.checked)} className="w-4 h-4 accent-[#572B7C]" />
              <Label htmlFor="gst" className="cursor-pointer text-sm">GST Registered</Label>
            </div>
            <div>
              <Label className={label}>Account Status</Label>
              <select value={formData.account_status} onChange={(e) => handleChange('account_status', e.target.value)} className={select}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="bounced">Email Bounced</option>
              </select>
            </div>
            <div>
              <Label className={label}>Approval Status</Label>
              <select value={formData.approval_status} onChange={(e) => handleChange('approval_status', e.target.value)} className={select}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className={label}>Bio</Label>
              <Textarea value={formData.bio} onChange={(e) => handleChange('bio', e.target.value)} rows={4} placeholder="User biography..." />
            </div>
          </div>

          {/* Role chips */}
          <div className="mt-4.5">
            <div className="text-xs font-bold text-[#2B3142] mb-2.5" style={{ fontFamily: "Poppins" }}>Roles</div>
            <div className="flex flex-wrap gap-2">
              {roleOptions.map((role) => {
                const on = formData.skills?.includes(role.value);
                return (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => handleRoleToggle(role.value)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
                    style={on
                      ? { color: "#572B7C", background: "#EDE4F4", borderColor: "#D9C5E6" }
                      : { color: "#8A90A2", background: "#F5F6F9", borderColor: "#E6E9EF" }}
                  >
                    {role.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Certifications */}
          <div className="mt-4.5 flex flex-col gap-2">
            <div className="text-xs font-bold text-[#2B3142] mb-1" style={{ fontFamily: "Poppins" }}>Certifications</div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={formData.wwcc} onChange={(e) => handleChange('wwcc', e.target.checked)} className="w-4 h-4 accent-[#572B7C]" />
              Working with Children Check (WWCC)
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={formData.casa_repl} onChange={(e) => handleChange('casa_repl', e.target.checked)} className="w-4 h-4 accent-[#572B7C]" />
              CASA RepL (Drone Operator)
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={formData.casa_reoc} onChange={(e) => handleChange('casa_reoc', e.target.checked)} className="w-4 h-4 accent-[#572B7C]" />
              CASA ReOc (Drone Operator)
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 mt-5 pt-4 border-t border-[#EEF0F4]">
            <button type="button" onClick={onClose} className="sz-btn sz-btn-outline">Cancel</button>
            <button type="submit" disabled={saving} className="sz-btn text-white" style={{ background: BLUE }}>
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-1.5" /> Save Changes</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUserEditModal;
